import { Box } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { ReviewChangeRequest } from "../../types";
import { ChangeRequestRow } from "./ChangeRequestRow";
import { CrSubmitterResolver } from "./useCrSubmitter";
import { ItemSummary } from "./useFlyerItemSummaries";
import { sortChangeRequestsForDisplay } from "./sortChangeRequests";

interface Props {
  changeRequests: ReviewChangeRequest[];
  busyIds: string[];
  flyerPublicationStatus?: string;
  itemSummaries: Map<string, ItemSummary | "loading">;
  reqOptions: { headers: { Authorization: string } };
  resolveSubmitter: CrSubmitterResolver;
  onAccept: (cr: ReviewChangeRequest) => void;
  onReject: (cr: ReviewChangeRequest) => void;
  onRestore: (cr: ReviewChangeRequest) => void;
  onHistory: (cr: ReviewChangeRequest) => void;
}

// Above this many change requests the list is windowed; below it the plain
// (non-scroll-bounded) layout is kept so the common small case is unchanged.
const VIRTUALIZE_THRESHOLD = 40;

export function ChangeRequestList({
  changeRequests,
  busyIds,
  flyerPublicationStatus,
  itemSummaries,
  reqOptions,
  resolveSubmitter,
  onAccept,
  onReject,
  onRestore,
  onHistory,
}: Props) {
  if (changeRequests.length === 0) return null;

  const sorted = sortChangeRequestsForDisplay(changeRequests);

  const renderRow = (cr: ReviewChangeRequest) => (
    <ChangeRequestRow
      key={cr.id}
      cr={cr}
      busy={busyIds.includes(cr.id)}
      flyerPublicationStatus={flyerPublicationStatus}
      itemSummaries={itemSummaries}
      reqOptions={reqOptions}
      resolveSubmitter={resolveSubmitter}
      onAccept={onAccept}
      onReject={onReject}
      onRestore={onRestore}
      onHistory={onHistory}
    />
  );

  if (sorted.length > VIRTUALIZE_THRESHOLD) {
    return (
      <Box data-testid="item-changes-section">
        <Virtuoso
          data={sorted}
          computeItemKey={(_, cr) => cr.id}
          overscan={600}
          style={{ height: "70vh" }}
          itemContent={(_, cr) => renderRow(cr)}
        />
      </Box>
    );
  }

  return (
    <Box data-testid="item-changes-section">{sorted.map(renderRow)}</Box>
  );
}
